// @/components/reusable/CountryFlag.jsx
/**
 * CountryFlag - Inline SVG flag component
 *
 * Renders country flags as inline SVGs bundled at build time.
 * No HTTP requests at runtime - flags appear instantly.
 *
 * @module components/reusable/CountryFlag
 */

import React, { memo, useMemo } from 'react';
import { getFlag } from '@/assets/flags';
import { normalizeCountryCode } from '@/utils/getCountryFlags';

/**
 * Blank flag SVG for fallback (simple gray circle)
 */
const BLANK_FLAG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><circle cx="256" cy="256" r="256" fill="#e5e7eb"/></svg>`;

/**
 * CountryFlag - Renders an inline SVG country flag
 *
 * @param {Object} props - Component props
 * @param {string} props.code - Country code (alpha-2, alpha-3, or alias)
 * @param {string} [props.className] - CSS classes for the container
 * @param {string} [props.size] - Predefined size: 'xs', 'sm', 'md', 'lg', 'xl' or custom
 * @param {boolean} [props.rounded=true] - Whether to apply rounded-full styling
 * @param {boolean} [props.border=true] - Whether to show border
 * @param {string} [props.alt] - Alt text (defaults to country code)
 * @returns {React.ReactNode}
 *
 * @example
 * // Basic usage
 * <CountryFlag code="DE" />
 *
 * @example
 * // With custom size
 * <CountryFlag code="US" className="size-8" />
 *
 * @example
 * // Using alpha-3 code
 * <CountryFlag code="DEU" size="lg" />
 */
const CountryFlag = memo(({
  code,
  className = '',
  size = 'md',
  rounded = true,
  border = true,
  alt
}) => {
  // Normalize the country code and get the SVG
  const svgContent = useMemo(() => {
    if (!code) return BLANK_FLAG;

    const normalizedCode = normalizeCountryCode(code);
    if (!normalizedCode) return BLANK_FLAG;

    const svg = getFlag(normalizedCode);
    return svg || BLANK_FLAG;
  }, [code]);

  // Build size classes based on preset or custom
  const sizeClasses = useMemo(() => {
    const sizes = {
      xs: 'size-4',
      sm: 'size-5',
      md: 'size-6',
      lg: 'size-8',
      xl: 'size-10'
    };
    return sizes[size] || '';
  }, [size]);

  // Build the complete className
  const containerClasses = useMemo(() => {
    const classes = ['inline-flex', 'overflow-hidden', 'shrink-0'];

    if (sizeClasses) classes.push(sizeClasses);
    if (rounded) classes.push('rounded-full');
    if (border) classes.push('border', 'border-zinc-200');
    if (className) classes.push(className);

    return classes.join(' ');
  }, [sizeClasses, rounded, border, className]);

  return (
    <span
      className={containerClasses}
      role="img"
      aria-label={alt || code || 'Country flag'}
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  );
});

CountryFlag.displayName = 'CountryFlag';

export default CountryFlag;
